import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import config from '../../config';
import { DAY, FIXED_DISCOUNT, MONTH, propTypes, WEEK } from '../../util/types';
import * as validators from '../../util/validators';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  EditListingActionButton,
  Form,
  FieldCurrencyInput,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  ValidationError,
} from '../../components';
import css from './EditListingPricingForm.css';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import get from 'lodash/get';

const { Money } = sdkTypes;

export const EditListingPricingFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        priceDurationConfig,
        typesOfDiscountConfig,
        typesOfDurationConfig,
        values,
        form,
        onBackWithoutSubmit,
      } = formRenderProps;

      const pricingTypeLabel = intl.formatMessage({
        id: 'EditListingPricingForm.pricingTypeLabel',
      });
      const pricingTypePlaceholder = intl.formatMessage({
        id: 'EditListingPricingForm.pricingTypePlaceholder',
      });

      const pricePerUnitMessage = intl.formatMessage({
        id: 'EditListingPricingForm.priceLabel',
      });

      const pricePlaceholderMessage = intl.formatMessage({
        id: 'EditListingPricingForm.priceInputPlaceholder',
      });

      const priceRequired = validators.required(
        intl.formatMessage({
          id: 'EditListingPricingForm.priceRequired',
        })
      );
      const minPrice = new Money(config.listingMinimumPriceSubUnits, config.currency);
      const minPriceRequired = validators.moneySubUnitAmountAtLeast(
        intl.formatMessage(
          {
            id: 'EditListingPricingForm.priceTooLow',
          },
          {
            minPrice: formatMoney(intl, minPrice),
          }
        ),
        config.listingMinimumPriceSubUnits
      );
      const priceValidators = config.listingMinimumPriceSubUnits
        ? validators.composeValidators(priceRequired, minPriceRequired)
        : priceRequired;

      const typeOfDiscountLabel = intl.formatMessage({
        id: 'EditListingPricingForm.typeOfDiscountLabel',
      });
      const typeOfDiscountPlaceholder = intl.formatMessage({
        id: 'EditListingPricingForm.typeOfDiscountPlaceholder',
      });

      const discountRatePercentageLabel = intl.formatMessage({
        id: 'EditListingPricingForm.discountRatePercentageLabel',
      });
      const discountRatePercentageWarning = intl.formatMessage({
        id: 'EditListingPricingForm.discountRatePercentageWarning',
      });

      const discountRateFixedLabel = intl.formatMessage({
        id: 'EditListingPricingForm.discountRateFixedLabel',
      });
      const discountRateFixedPlaceHolder = intl.formatMessage({
        id: 'EditListingPricingForm.discountRateFixedPlaceHolder',
      });

      const typeOfDurationLabel = intl.formatMessage({
        id: 'EditListingPricingForm.typeOfDurationLabel',
      });
      const typeOfDurationPlaceholder = intl.formatMessage({
        id: 'EditListingPricingForm.typeOfDurationPlaceholder',
      });
      const durationLabel = intl.formatMessage({ id: 'EditListingPricingForm.durationLabel' });

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;

      const { updateListingError, showListingsError } = fetchErrors || {};

      const requiredMessage = intl.formatMessage({ id: 'EditListingPricingForm.requiredMessage' });
      const durationMessage = intl.formatMessage({ id: 'EditListingPricingForm.durationMessage' });
      const durationsMessage = intl.formatMessage({
        id: 'EditListingPricingForm.durationsMessage',
      });

      const minRentLabelMessage = intl.formatMessage({
        id: 'EditListingPricingForm.minRentLabel',
      });

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          {updateListingError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingPricingForm.updateFailed" />
            </p>
          ) : null}
          {showListingsError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingPricingForm.showListingFailed" />
            </p>
          ) : null}

          <div className={css.priceWrapper}>
            <FieldCurrencyInput
              id="price"
              name="price"
              className={css.priceInput}
              autoFocus
              label={pricePerUnitMessage}
              placeholder={pricePlaceholderMessage}
              currencyConfig={config.currencyConfig}
              validate={priceValidators}
            />

            <FieldSelect
              className={css.pricingType}
              name={'pricingType'}
              id={'pricingType'}
              label={pricingTypeLabel}
            >
              <option disabled value="">
                {pricingTypePlaceholder}
              </option>
              {priceDurationConfig.map(c => (
                <option key={c.key} value={c.key}>
                  {intl.formatMessage({ id: c.labelId })}
                </option>
              ))}
            </FieldSelect>
          </div>

          <div className={css.minRentWrapper}>
            <FieldTextInput
              id="minRent"
              name="minRent"
              className={css.minRent}
              type="number"
              label={minRentLabelMessage}
              step="1"
              min="1"
            />

            <p className={css.rentType}>{`${values.pricingType}${values.minRent <= 1 ? '' : 's'}`}</p>
          </div>

          <FieldArray name="discounts" validate={validators.validateDiscounts(durationsMessage)}>
            {({ fields, meta }) => {
              const { error } = meta;
              const touched = !!error && typeof error === 'string';
              const fieldMeta = { touched, error };

              return (
                <>
                  {fields.map((name, index) => {
                    const typeOfDuration = get(values, `${name}.typeOfDuration`, DAY);
                    const typeOfDurationObj = typesOfDurationConfig.find(
                      ele => ele.key === typeOfDuration
                    );
                    const typeOfDurationText = intl.formatMessage({
                      id: typeOfDurationObj.labelId,
                    });
                    const typeOfDiscount = get(values, `${name}.typeOfDiscount`, FIXED_DISCOUNT);
                    const duration = get(values, `${name}.duration`, null);

                    return (
                      <div className={css.fieldItem} key={name}>
                        <div className={css.removeButton}>
                          <InlineTextButton type="button" onClick={() => fields.remove(index)}>
                            <FormattedMessage id="EditListingPricingForm.remove" />
                          </InlineTextButton>
                          <div className={css.priceWrapper}>
                            <FieldSelect
                              className={css.typesOfDiscount}
                              name={`${name}.typeOfDiscount`}
                              id={`${name}.typeOfDiscount`}
                              label={typeOfDiscountLabel}
                              validate={validators.required(requiredMessage)}
                            >
                              <option disabled value="">
                                {typeOfDiscountPlaceholder}
                              </option>
                              {typesOfDiscountConfig.map(c => (
                                <option key={c.key} value={c.key}>
                                  {intl.formatMessage({ id: c.labelId })}
                                </option>
                              ))}
                            </FieldSelect>
                            {typeOfDiscount === FIXED_DISCOUNT ? (
                              <FieldCurrencyInput
                                id={`${name}.discountPrice`}
                                name={`${name}.discountPrice`}
                                className={css.discountRate}
                                label={discountRateFixedLabel}
                                placeholder={discountRateFixedPlaceHolder}
                                currencyConfig={config.currencyConfig}
                                validate={validators.required(requiredMessage)}
                              />
                            ) : (
                              <FieldTextInput
                                className={css.discountRate}
                                id={`${name}.discountRate`}
                                name={`${name}.discountRate`}
                                type="number"
                                min={0}
                                max={99}
                                label={discountRatePercentageLabel}
                                validate={validators.validateDiscountRatePercentage(
                                  discountRatePercentageWarning
                                )}
                              />
                            )}
                          </div>

                          <div className={css.priceWrapper}>
                            <FieldSelect
                              className={css.typesOfDiscount}
                              name={`${name}.typeOfDuration`}
                              id={`${name}.typeOfDuration`}
                              label={typeOfDurationLabel}
                              validate={validators.required(requiredMessage)}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (duration) {
                                  let day = 1;
                                  if (value === WEEK) {
                                    day = config.custom.ONE_WEEK;
                                  }
                                  if (value === MONTH) {
                                    day = config.custom.ONE_MONTH;
                                  }
                                  const durationByDay = Number(duration) * day;
                                  form.batch(() => {
                                    form.change(`${name}.typeOfDuration`, value);
                                    form.change(`${name}.durationByDay`, durationByDay);
                                  });
                                } else {
                                  form.change(`${name}.typeOfDuration`, value);
                                }
                              }}
                            >
                              <option disabled value="">
                                {typeOfDurationPlaceholder}
                              </option>
                              {typesOfDurationConfig.map(c => (
                                <option key={c.key} value={c.key}>
                                  {intl.formatMessage({ id: c.labelId })}
                                </option>
                              ))}
                            </FieldSelect>
                            <div className={css.durationWrapper}>
                              <FieldTextInput
                                className={css.duration}
                                id={`${name}.duration`}
                                name={`${name}.duration`}
                                type="number"
                                min={0}
                                label={durationLabel}
                                onChange={e => {
                                  const value = Number(e.target.value);
                                  let day = 1;
                                  if (typeOfDuration === WEEK) {
                                    day = config.custom.ONE_WEEK;
                                  }
                                  if (typeOfDuration === MONTH) {
                                    day = config.custom.ONE_MONTH;
                                  }
                                  const durationByDay = value * day;
                                  form.batch(() => {
                                    form.change(`${name}.duration`, value);
                                    form.change(`${name}.durationByDay`, durationByDay);
                                  });
                                }}
                                validate={validators.validateDuration(durationMessage)}
                              />
                              <div className={css.discountType}>{typeOfDurationText}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className={css.validator}>
                    <ValidationError fieldMeta={fieldMeta} />
                  </div>
                  <div className={css.buttonWrapper} onClick={() => fields.push(null)}>
                    <div className={css.plusIcon}></div>
                    <div className={css.buttonContent}>
                      <FormattedMessage id="EditListingPricingForm.addDiscount" />
                    </div>
                  </div>
                </>
              );
            }}
          </FieldArray>

          <p className={css.guidanceContent}>
            <FormattedMessage id="EditListingPricingForm.guidanceMessage" />
          </p>

          <EditListingActionButton
            invalid={invalid}
            nextBtnMessage={saveActionMsg}
            nextBtnClassName={css.submitButton}
            form={form}
            onBackWithoutSubmit={onBackWithoutSubmit}
            submitInProgress={submitInProgress}
            submitReady={submitReady}
            submitDisabled={submitDisabled}
          />
        </Form>
      );
    }}
  />
);

EditListingPricingFormComponent.defaultProps = { fetchErrors: null };

EditListingPricingFormComponent.propTypes = {
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingPricingFormComponent);
